import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import PrintIcon from "@material-ui/icons/Print";
import EditIcon from "@material-ui/icons/Edit";
import { OrderPanelDialog } from "./Components/OrderPanelDialog";
import { OrderItemDialog } from "./Components/OrderItemDialog";
import { OrderActionDialog } from "./Components/OrderActionDialog";
import { OrderCaptureDialog } from "./Components/OrderCaptureDialog";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import PhoneIcon from "@material-ui/icons/Phone";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPost, authPut } from "../../lib";

const STRENGTH_COLOR = { EXACT: "#c00", STRONG: "#e67e22", WEAK: "#7f8c8d" };

/** Weak matches are badged per match type, in the legacy page's colours. */
const MATCH_COLOR = {
  email: "#8e44ad",
  phone: "#2980b9",
  name: "#27ae60",
  ip: "#7f8c8d",
};

/**
 * Flagged on the contact card so an internal test order is not mistaken for a
 * customer's. Taken from the legacy order page — note report.php carries a
 * DIFFERENT pair (71.167.41.8, 24.89.179.250), which looks like drift rather
 * than intent.
 */
const OFFICE_IPS = ["71.190.249.242"];

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1200, margin: "24px auto 0", width: "100%" },
  navBar: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  navSide: { display: "flex", gap: theme.spacing(1), flex: "1 1 0", minWidth: 0 },
  navRight: { justifyContent: "flex-end" },
  orderLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    flex: "0 0 auto",
  },
  /** Inert on screen; print gives it flex:1 to split number from timestamp. */
  orderLineSpacer: { flex: "0 0 0px" },
  orderNo: { fontSize: 19, fontWeight: 500, lineHeight: 1.2, whiteSpace: "nowrap" },
  orderDate: {
    fontSize: 15,
    fontWeight: 500,
    color: "rgba(0,0,0,0.82)",
    whiteSpace: "nowrap",
  },
  preTag: {
    marginLeft: 8,
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: ".05em",
    color: "#4a148c",
    background: "rgba(74,20,140,0.10)",
    verticalAlign: "middle",
  },
  printBtn: { marginLeft: 4, padding: 3 },
  title: { fontSize: 20, fontWeight: 500 },
  spacer: { flex: 1 },
  /** Full-width strips: payment and gift carry more fields than a third fits. */
  wide: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  wideGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    fontSize: 13.5,
    lineHeight: 1.7,
  },
  field: { minWidth: 150 },
  warn: { color: "#b71c1c", fontWeight: 700 },
  similarRow: { fontSize: 13, lineHeight: 1.9 },
  link: { color: "#315179", textDecoration: "underline", cursor: "pointer", fontWeight: 600 },
  badge: {
    marginLeft: 8,
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: 10.5,
    fontWeight: 700,
    color: "#fff",
    letterSpacing: ".04em",
  },
  pending: {
    padding: theme.spacing(2),
    textAlign: "center",
    fontSize: 13,
    color: theme.palette.text.secondary,
    border: "1px dashed rgba(0,0,0,0.18)",
    borderRadius: 3,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  panel: { padding: theme.spacing(2) },
  panelTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.25),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editBtn: { padding: 2, color: "rgba(0,0,0,0.4)", "&:hover": { color: "#315179" } },
  line: { fontSize: 13.5, lineHeight: 1.65 },
  muted: { color: theme.palette.text.secondary },
  cell: { fontSize: 13, padding: "7px 10px" },
  num: { fontVariantNumeric: "tabular-nums", textAlign: "right" },
  itemLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  totals: { marginLeft: "auto", width: 320, marginTop: theme.spacing(1.5) },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 13.5,
    padding: "1px 0",
    minHeight: 24,
  },
  /* Figures sit to the left of the pencil column; rows without a pencil are
     padded by the same amount so the numbers stay in one line. */
  totalValue: { display: "flex", alignItems: "center", gap: 2 },
  totalPad: { paddingRight: 24 },
  grand: {
    borderTop: "1px solid rgba(0,0,0,0.12)",
    marginTop: 6,
    paddingTop: 8,
    fontWeight: 700,
    fontSize: 15,
  },
  notes: { whiteSpace: "pre-wrap", fontSize: 13.5, lineHeight: 1.6 },
  notesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: theme.spacing(2),
    // The Items card above uses `panel`, which carries no bottom margin.
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  actionRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  payNote: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },
  payNoteRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: 2,
  },
  /* Reads as a filled, uneditable field: the note is changed through the
     pencil or the box beside it, never by clicking the text.
     minWidth:0 lets a long note wrap instead of pushing the input over. */
  payNoteText: {
    flex: "1 1 50%",
    minWidth: 0,
    fontSize: 13.5,
    lineHeight: 1.5,
    minHeight: 38,
    padding: "8px 10px",
    background: "rgba(0,0,0,0.05)",
    border: "1px solid rgba(0,0,0,0.14)",
    borderRadius: 3,
    color: "rgba(0,0,0,0.72)",
    cursor: "default",
    wordBreak: "break-word",
  },
  payNoteAdd: {
    flex: "1 1 50%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  noteHint: {
    fontSize: 11.5,
    color: theme.palette.text.secondary,
    marginTop: -6,
    marginBottom: theme.spacing(1),
  },
  noteAdd: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  /* Fixed height with its own scroll: an order with fifty notes should not
     push everything below it off the screen. */
  noteLog: {
    whiteSpace: "pre-wrap",
    fontSize: 12.5,
    lineHeight: 1.65,
    maxHeight: 190,
    overflowY: "auto",
    padding: theme.spacing(1),
    background: "rgba(0,0,0,0.02)",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 3,
  },
  contactFoot: {
    marginTop: theme.spacing(1.25),
    paddingTop: theme.spacing(1),
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },
  prevCust: {
    display: "flex",
    alignItems: "center",
    fontSize: 13.5,
    cursor: "pointer",
    userSelect: "none",
  },
  /* No ripple backdrop: it reads as a heavy blue box behind the tick. */
  prevBox: {
    padding: "0 6px 0 0",
    backgroundColor: "transparent !important",
    "& .MuiTouchRipple-root": { display: "none" },
    "&:focus": { outline: "none" },
  },
  prevRow: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
  iconRow: { display: "flex", alignItems: "center", gap: 6 },
  rowIcon: { fontSize: 15, color: "rgba(0,0,0,0.42)", flex: "none" },
  mailLink: {
    color: "inherit",
    textDecoration: "none",
    borderBottom: "1px solid transparent",
    "&:hover": { color: "#315179", borderBottomColor: "rgba(49,81,121,0.5)" },
  },
  copyBtn: {
    padding: 2,
    color: "rgba(0,0,0,0.32)",
    "&:hover": { color: "#315179", backgroundColor: "transparent" },
  },
  copied: { fontSize: 11, color: "#1b5e20" },
  similarBadge: {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: 10.5,
    fontWeight: 700,
    color: "#fff",
    letterSpacing: ".04em",
    cursor: "pointer",
  },
  officeIp: { color: "#b71c1c", fontWeight: 700 },
  checks: { display: "flex", flexDirection: "column", fontSize: 12.5, lineHeight: 1.5 },
  pass: { color: "#1b5e20" },
  fail: { color: "#b71c1c" },
  error: { color: "#c62828" },
}));

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString();
};

/**
 * An email address on the contact card: click to compose, or copy it.
 *
 * The copy button is the legacy card's `copy-btn`. clipboard.writeText needs a
 * secure context, so there is a textarea fallback for plain-HTTP environments.
 */
function EmailLine({ address, label }) {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
    } catch (_) {
      const box = document.createElement("textarea");
      box.value = address;
      box.style.position = "fixed";
      box.style.opacity = "0";
      document.body.appendChild(box);
      box.select();
      document.execCommand("copy");
      box.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <div className={classes.iconRow}>
      <MailOutlineIcon className={classes.rowIcon} titleAccess={label} />
      <a className={classes.mailLink} href={`mailto:${address}`} title={`Email ${address}`}>
        {address}
      </a>
      <IconButton
        size="small"
        className={`${classes.copyBtn} no-print`}
        title="Copy email address"
        onClick={copy}
      >
        <FileCopyOutlinedIcon style={{ fontSize: 13 }} />
      </IconButton>
      {copied && <span className={classes.copied}>Copied</span>}
    </div>
  );
}

/** Name, business, street, city/state/zip, country — blanks omitted. */
function Address({ p, prefix }) {
  const name = [p[`${prefix}_first_name`], p[`${prefix}_mi`], p[`${prefix}_last_name`]]
    .filter((x) => x && x !== "x")
    .join(" ");
  const cityLine = [p[`${prefix}_city`], p[`${prefix}_state`]].filter(Boolean).join(", ");

  return (
    <>
      {name && <div>{name}</div>}
      {p[`${prefix}_business_name`] && <div>{p[`${prefix}_business_name`]}</div>}
      {p[`${prefix}_address1`] && <div>{p[`${prefix}_address1`]}</div>}
      {p[`${prefix}_address2`] && <div>{p[`${prefix}_address2`]}</div>}
      {(cityLine || p[`${prefix}_zip`]) && (
        <div>{[cityLine, p[`${prefix}_zip`]].filter(Boolean).join(" ")}</div>
      )}
      {p[`${prefix}_country`] && <div>{p[`${prefix}_country`]}</div>}
    </>
  );
}

/**
 * The payment note.
 *
 * Unlike the note logs, this is one running sentence: submitting a second note
 * appends it to the first with a space rather than starting a stamped line, so
 * "test" then "second test" reads "test second test". The pencil rewrites it
 * and clearing it in that dialog removes it.
 */
function PaymentNote({ orderId, value, onSaved, onEdit }) {
  const classes = useStyles();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    const { error: err } = await authPost(`/orders/${orderId}/notes`, {
      field: "notes_payment",
      text: text.trim(),
    });
    setBusy(false);
    if (err) {
      setError(err.message || "Could not add that note.");
      return;
    }
    setText("");
    setError("");
    onSaved();
  };

  return (
    <div className={classes.payNote}>
      <div className={classes.muted} style={{ fontSize: 12 }}>
        Payment note
        <IconButton
          size="small"
          className={`${classes.editBtn} no-print`}
          title="Edit or clear the payment note"
          onClick={onEdit}
        >
          <EditIcon style={{ fontSize: 14 }} />
        </IconButton>
      </div>

      {/* Note on the left, the add box on the right, half each — the note is
          a short running sentence and never needs a row of its own. */}
      <div className={classes.payNoteRow}>
        <div className={classes.payNoteText}>
          {value || <span className={classes.muted}>—</span>}
        </div>

        <div className={`${classes.payNoteAdd} no-print`}>
          <TextField
            placeholder="Add to the payment note…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            style={{ flex: 1 }}
          />
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={busy || !text.trim()}
            onClick={add}
          >
            {busy ? "Adding…" : "Add"}
          </Button>
        </div>
      </div>

      {error && <div className={classes.error} style={{ fontSize: 12.5 }}>{error}</div>}
    </div>
  );
}

/**
 * One notes card: an "add" box that prepends a stamped line, and the running
 * log beneath it.
 *
 * Adding and rewriting are separate on purpose. Adding is the everyday action
 * and is append-only, so the record of who said what and when survives; the
 * pencil is for correcting a mistake, and rewrites the field wholesale.
 */
function NoteCard({ title, hint, field, value, orderId, readOnly, onSaved, onEdit }) {
  const classes = useStyles();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    const { error: err } = await authPost(`/orders/${orderId}/notes`, { field, text });
    setBusy(false);
    if (err) {
      setError(err.message || "Could not add that note.");
      return;
    }
    setText("");
    setError("");
    onSaved();
  };

  return (
    <Paper className={classes.panel}>
      <div className={classes.panelTitle}>
        <span>{title}</span>
        {!readOnly && (
          <IconButton
            size="small"
            className={`${classes.editBtn} no-print`}
            title="Rewrite these notes"
            onClick={onEdit}
          >
            <EditIcon style={{ fontSize: 15 }} />
          </IconButton>
        )}
      </div>
      <div className={`${classes.noteHint} no-print`}>{hint}</div>

      {!readOnly && (
        <div className={`${classes.noteAdd} no-print`}>
          <TextField
            multiline
            rows={2}
            fullWidth
            variant="outlined"
            placeholder="Add a note…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={busy || !text.trim()}
            onClick={add}
          >
            {busy ? "Adding…" : "Add Note"}
          </Button>
        </div>
      )}

      {error && <div className={classes.error} style={{ fontSize: 12.5 }}>{error}</div>}

      <div className={`${classes.noteLog} print-notes`}>
        {value ? value : <span className={classes.muted}>No notes yet.</span>}
      </div>
    </Paper>
  );
}

/** Read-only order detail. Editing arrives in the next phase. */
export function OrderDetailPage() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [similar, setSimilar] = useState(null);
  const [editing, setEditing] = useState(null);
  const [shipRates, setShipRates] = useState([]);
  const [pay, setPay] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [acting, setActing] = useState(null);

  const go = (target) => () => history.push(`/orders/${target}`);

  /** Optimistic, with the previous value restored if the write fails. */
  const setPreviousCustomer = async (checked) => {
    const before = data.order.previous_customer;
    setData((d) => ({ ...d, order: { ...d.order, previous_customer: checked ? 1 : 0 } }));

    const { error: err } = await authPut(`/orders/${id}`, { previous_customer: checked });
    if (err) {
      setData((d) => ({ ...d, order: { ...d.order, previous_customer: before } }));
      setError(err.message || "Could not save that change.");
    }
  };

  /**
   * `silent` refetches without unmounting the page.
   *
   * Toggling `loading` swaps the whole screen for a spinner, so a save would
   * throw the reader back to the top of the page — the note they just added
   * is halfway down it.
   */
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const { data: res, error: err } = await authGet(`/orders/${id}`);
    if (err) setError(err.message || "Could not load this order.");
    else setData(res);
    if (!silent) setLoading(false);
  }, [id]);

  const refresh = useCallback(() => load({ silent: true }), [load]);

  useEffect(() => {
    load();
  }, [load]);

  /*
   * Card details come from the payment gateway, not our tables. Wherever no
   * provider is configured the endpoint answers { available: false } and the
   * three fields below simply do not render — which is how PT keeps the card
   * it has today.
   */
  useEffect(() => {
    let cancelled = false;
    setPay(null);
    authGet(`/orders/${id}/payment-details`).then(({ data }) => {
      if (!cancelled) setPay(data ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Fetched once for the shipping-method selector.
  useEffect(() => {
    authGet("/orders/ship-rates").then(({ data }) => setShipRates(data?.rows ?? []));
  }, []);

  // Separate request: it scans the order table, so the page should not wait.
  useEffect(() => {
    let cancelled = false;
    setSimilar(null);
    authGet(`/orders/${id}/similar`).then(({ data, error: err }) => {
      if (!cancelled) setSimilar(err ? [] : data?.rows ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <Box className={classes.page} textAlign="center" pt={6}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !data?.order) {
    return (
      <Box className={classes.page}>
        <Paper className={classes.panel}>
          <Typography className={classes.error}>{error || "Order not found."}</Typography>
          <Box mt={2}>
            <Button size="small" variant="outlined" onClick={() => history.push("/orders")}>
              Back to orders
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const { order, bill, ship, items, prevId, nextId } = data;
  const itemsTotal = items.reduce(
    (sum, i) => sum + Number(i.price || 0) * Number(i.qty || 1) * (Number(i.is_credit) ? -1 : 1),
    0,
  );
  const grand =
    itemsTotal +
    Number(order.shipping || 0) +
    Number(order.tax || 0) +
    Number(order.surcharge || 0) -
    Number(order.discount || 0) -
    Number(order.wire_discount || 0);


  return (
    <Box className={`${classes.page} order-print`}>
      {/* Previous left, Next right, the order itself centred — the legacy
          page's arrangement. It walked id ± 1; these come from the server as
          the actual neighbouring ids, since the sequence has gaps. */}
      <div className={classes.navBar}>
        <div className={`${classes.navSide} no-print`}>
          <Button
            size="small"
            variant="outlined"
            disabled={!prevId}
            onClick={prevId ? go(prevId) : undefined}
          >
            ← Previous
          </Button>
          <Button size="small" variant="outlined" onClick={() => history.push("/orders")}>
            All Orders
          </Button>
        </div>

        {/* Order number leads the row; the timestamp is pushed to its end. */}
        <div className={`${classes.orderLine} print-orderline`}>
          <span className={classes.orderNo}>Order #: {order.order_id}</span>
          {!!Number(order.is_preowned) && <span className={classes.preTag}>PRE-OWNED</span>}
          <span className={`${classes.orderLineSpacer} print-orderline-spacer`} />
          <span className={classes.orderDate}>{when(order.order_time)}</span>
          <IconButton
            size="small"
            title="Print this order"
            className={`${classes.printBtn} no-print`}
            onClick={() => window.print()}
          >
            <PrintIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={`${classes.navSide} ${classes.navRight} no-print`}>
          {/* History lives on its own page, as a watch's does, rather than as
              a slab at the bottom of this one. */}
          <Button
            size="small"
            variant="outlined"
            onClick={() => history.push(`/orders/${order.order_id}/history`)}
          >
            History
          </Button>
          {/* Copies this customer's details into a fresh order. */}
          <Button
            size="small"
            variant="outlined"
            title="New order for this customer"
            onClick={() => history.push(`/orders/new?from=${order.order_id}`)}
          >
            New
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!nextId}
            onClick={nextId ? go(nextId) : undefined}
          >
            Next →
          </Button>
        </div>
      </div>

      <div className={`${classes.grid} print-grid`}>
        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>
            <span>Billing</span>
            <IconButton size="small" className={`${classes.editBtn} no-print`}
                        title="Edit" onClick={() => setEditing("billing")}>
              <EditIcon style={{ fontSize: 15 }} />
            </IconButton>
          </div>
          <div className={classes.line}>
            {bill ? <Address p={bill} prefix="bill" /> : <span className={classes.muted}>—</span>}
          </div>
        </Paper>

        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>
            <span>Shipping</span>
            <IconButton size="small" className={`${classes.editBtn} no-print`}
                        title="Edit" onClick={() => setEditing("shipping")}>
              <EditIcon style={{ fontSize: 15 }} />
            </IconButton>
          </div>
          <div className={classes.line}>
            {ship ? <Address p={ship} prefix="ship" /> : <span className={classes.muted}>—</span>}
          </div>
          {!!ship?.gift_wrap && <div className={classes.muted}>Gift wrapped</div>}
          {ship?.gift_message && <div className={classes.muted}>“{ship.gift_message}”</div>}
        </Paper>

        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>
            <span>Contact</span>
            <IconButton size="small" className={`${classes.editBtn} no-print`}
                        title="Edit" onClick={() => setEditing("contact")}>
              <EditIcon style={{ fontSize: 15 }} />
            </IconButton>
          </div>
          {/* Icons rather than labels: four short rows read faster than four
              "Label: value" pairs, and the tooltips keep the meaning. */}
          <div className={classes.line}>
            {bill?.email && <EmailLine address={bill.email} label="Email" />}
            {bill?.email_secondary && (
              <EmailLine address={bill.email_secondary} label="Secondary email" />
            )}
            <div className={classes.iconRow}>
              <PhoneIcon className={classes.rowIcon} titleAccess="Home phone" />
              {bill?.home_phone || "—"}
            </div>
            <div className={classes.iconRow}>
              <PhoneAndroidIcon className={classes.rowIcon} titleAccess="Contact phone" />
              {bill?.contact_phone || "—"}
            </div>
          </div>

          <div className={classes.contactFoot}>
            <div className={classes.prevRow}>
            <label className={classes.prevCust}>
              <Checkbox
                size="small"
                color="primary"
                className={classes.prevBox}
                checked={!!Number(order.previous_customer)}
                onChange={(e) => setPreviousCustomer(e.target.checked)}
              />
              Previous customer
            </label>

            {/* Jumps to the Similar Customers card below, as the legacy badge
                did. Coloured by the strongest match found. */}
            {!!similar?.length && (
              <span
                className={classes.similarBadge}
                style={{
                  background:
                    STRENGTH_COLOR[
                      similar.some((o) => o.strength === "EXACT")
                        ? "EXACT"
                        : similar.some((o) => o.strength === "STRONG")
                        ? "STRONG"
                        : "WEAK"
                    ],
                }}
                onClick={() =>
                  document
                    .getElementById("order-similar")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                SIMILAR
              </span>
            )}
            </div>

            <div className={classes.line} style={{ marginTop: 6 }}>
              <span className={classes.muted}>IP: </span>
              {order.ip || "—"}
              {OFFICE_IPS.includes(String(order.ip)) && (
                <span className={classes.officeIp}> Office IP</span>
              )}
            </div>
            <div className={classes.line}>
              <span className={classes.muted}>MaxMind: </span>
              <span className={classes.muted}>pending</span>
            </div>
          </div>
        </Paper>

      </div>

      {/* Payment runs the full width: it carries the method, reference, coupon
          and status flags, which do not fit a third of a row. The card iframe
          and expiry from the legacy page are deliberately absent — the expiry
          columns are pre-tokenisation leftovers and the iframe exposed card
          data through an internal host. */}
      <Paper className={classes.wide}>
        <div className={classes.panelTitle}>
          <span>Payment</span>
          {/* Only where a gateway is wired: PT has none, so nothing shows. */}
          {pay && pay.available === false && pay.reason?.startsWith("No payment provider")
            ? null
            : !order.payment_ref && (
                <Button
                  size="small"
                  variant="outlined"
                  className="no-print"
                  onClick={() => setCapturing(true)}
                >
                  Take payment
                </Button>
              )}
        </div>
        <div className={classes.wideGrid}>
          <div className={classes.field}>
            <div className={classes.muted} style={{ fontSize: 12 }}>Method</div>
            <div>{order.pay_method || "—"}</div>
          </div>

          {pay?.available && (
            <>
              <div className={classes.field}>
                <div className={classes.muted} style={{ fontSize: 12 }}>Card</div>
                <div>
                  {pay.cardType ? `${pay.cardType} ` : ""}
                  {pay.last4 ? `•••• ${pay.last4}` : "—"}
                </div>
              </div>

              <div className={classes.field}>
                <div className={classes.muted} style={{ fontSize: 12 }}>Expires</div>
                <div>
                  {pay.expMonth && pay.expYear ? `${pay.expMonth}/${pay.expYear}` : "—"}
                </div>
              </div>

              <div className={classes.field}>
                <div className={classes.muted} style={{ fontSize: 12 }}>Auth checks</div>
                <div className={classes.checks}>
                  <span className={pay.checks.cvv === "Matched" ? classes.pass : classes.fail}>
                    CVV: {pay.checks.cvv ?? "—"}
                  </span>
                  <span
                    className={pay.checks.avsPostal === "Matched" ? classes.pass : classes.fail}
                  >
                    AVS zip: {pay.checks.avsPostal ?? "—"}
                  </span>
                  {pay.checks.processor && (
                    <span className={classes.muted}>{pay.checks.processor}</span>
                  )}
                  {pay.status && <span className={classes.muted}>Status: {pay.status}</span>}
                </div>
              </div>
            </>
          )}

          {order.payment_ref && (
            <div className={classes.field}>
              <div className={classes.muted} style={{ fontSize: 12 }}>Reference</div>
              <div>{order.payment_ref}</div>
            </div>
          )}

          {order.netcents_status && (
            <div className={classes.field}>
              <div className={classes.muted} style={{ fontSize: 12 }}>NetCents</div>
              <div>{order.netcents_status}</div>
            </div>
          )}

          <div className={classes.field}>
            <div className={classes.muted} style={{ fontSize: 12 }}>
              Coupon
              <IconButton
                size="small"
                className={`${classes.editBtn} no-print`}
                title="Change or remove the coupon"
                onClick={() => setEditing("payment")}
              >
                <EditIcon style={{ fontSize: 14 }} />
              </IconButton>
            </div>
            <div>
              {order.coupon_code
                ? `${order.coupon_code}${order.coupon_name ? ` — ${order.coupon_name}` : ""}`
                : "—"}
            </div>
          </div>

          {/* Overrides the standard return window for this order. */}
          <div className={classes.field}>
            <div className={classes.muted} style={{ fontSize: 12 }}>
              Return expires
              <IconButton
                size="small"
                className={`${classes.editBtn} no-print`}
                title="Set the return expiry date"
                onClick={() => setEditing("payment")}
              >
                <EditIcon style={{ fontSize: 14 }} />
              </IconButton>
            </div>
            <div>
              {order.return_end ? String(order.return_end).slice(0, 10) : "—"}
            </div>
          </div>

          {order.amazon_order_no && (
            <div className={classes.field}>
              <div className={classes.muted} style={{ fontSize: 12 }}>Amazon order</div>
              <div>{order.amazon_order_no}</div>
            </div>
          )}
        </div>

        <PaymentNote
          orderId={order.order_id}
          value={order.notes_payment}
          onSaved={refresh}
          onEdit={() => setEditing("payment")}
        />
      </Paper>

      {/* Gift and instructions, as the legacy page grouped them. */}
      <Paper className={classes.wide}>
        <div className={classes.panelTitle}>
            <span>Gift &amp; Instructions</span>
            <IconButton size="small" className={`${classes.editBtn} no-print`}
                        title="Edit" onClick={() => setEditing("gift")}>
              <EditIcon style={{ fontSize: 15 }} />
            </IconButton>
          </div>
        <div className={classes.wideGrid}>
          <div className={classes.field}>
            <div className={classes.muted} style={{ fontSize: 12 }}>Gift wrap</div>
            <div>{Number(ship?.gift_wrap) ? "Yes" : "No"}</div>
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className={classes.muted} style={{ fontSize: 12 }}>Gift card</div>
            <div className={classes.notes}>{ship?.gift_message || "—"}</div>
          </div>
        </div>
        <Box mt={1.5} className={classes.wideGrid}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className={classes.muted} style={{ fontSize: 12 }}>Special instructions</div>
            <div className={classes.notes}>{bill?.special_instructions || "—"}</div>
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className={classes.muted} style={{ fontSize: 12 }}>Sizing instructions</div>
            <div className={classes.notes}>{order.sizing_instructions || "—"}</div>
          </div>
        </Box>
      </Paper>

      <Paper className={classes.panel}>
        <div className={classes.panelTitle}>
          <span>Items</span>
        </div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.cell}>Model</TableCell>
              <TableCell className={classes.cell}>Description</TableCell>
              <TableCell className={`${classes.cell} ${classes.num}`} style={{ width: 60 }}>
                Qty
              </TableCell>
              <TableCell className={`${classes.cell} ${classes.num}`} style={{ width: 120 }}>
                Price
              </TableCell>
              <TableCell className={`${classes.cell} no-print`} style={{ width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.order_item_id}>
                <TableCell className={classes.cell}>
                  {i.url ? (
                    <a
                      className={classes.itemLink}
                      href={i.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open this item on the site"
                    >
                      {i.model_no}
                    </a>
                  ) : (
                    i.model_no
                  )}
                </TableCell>
                <TableCell className={classes.cell}>
                  {i.title}
                  {!!Number(i.is_credit) && <span className={classes.muted}> · credit</span>}
                  {!!Number(i.special_order) && <span className={classes.muted}> · special order</span>}
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.num}`}>{i.qty}</TableCell>
                <TableCell className={`${classes.cell} ${classes.num}`}>
                  {money(Number(i.price) * (Number(i.is_credit) ? -1 : 1))}
                </TableCell>
                <TableCell className={`${classes.cell} no-print`}>
                  <IconButton
                    size="small"
                    className={classes.editBtn}
                    title="Edit this item"
                    onClick={() => setEditingItem(i)}
                  >
                    <EditIcon style={{ fontSize: 15 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!items.length && (
              <TableRow>
                <TableCell className={classes.cell} colSpan={5}>
                  <span className={classes.muted}>No line items on this order.</span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <div className={classes.totals}>
          {/* Subtotal is the line items alone; the adjustments below it are
              what the pencil on this row edits. */}
          <div className={classes.totalRow}>
            <span>Subtotal</span>
            <span className={classes.totalValue}>
              {money(itemsTotal)}
              <IconButton
                size="small"
                className={`${classes.editBtn} no-print`}
                title="Edit discounts and surcharge"
                onClick={() => setEditing("adjustments")}
              >
                <EditIcon style={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </div>
          <div className={classes.totalRow}>
            <span>Shipping</span>
            <span className={classes.totalValue}>
              {money(order.shipping)}
              <IconButton
                size="small"
                className={`${classes.editBtn} no-print`}
                title="Edit shipping method"
                onClick={() => setEditing("method")}
              >
                <EditIcon style={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </div>
          <div className={classes.totalRow}>
            <span>Tax</span>
            <span className={classes.totalPad}>{money(order.tax)}</span>
          </div>
          {Number(order.surcharge) > 0 && (
            <div className={classes.totalRow}>
              <span>Surcharge</span>
              <span className={classes.totalPad}>{money(order.surcharge)}</span>
            </div>
          )}
          {Number(order.discount) > 0 && (
            <div className={classes.totalRow}>
              <span>Discount</span>
              <span className={classes.totalPad}>−{money(order.discount)}</span>
            </div>
          )}
          {Number(order.wire_discount) > 0 && (
            <div className={classes.totalRow}>
              <span>Wire discount</span>
              <span className={classes.totalPad}>−{money(order.wire_discount)}</span>
            </div>
          )}
          <div className={`${classes.totalRow} ${classes.grand}`}>
            <span>Total</span>
            <span className={classes.totalPad}>{money(grand)}</span>
          </div>
        </div>
      </Paper>

      {/* The four legacy actions. They only send an email and log it — none of
          them changes the order's status, which is a separate action on the
          list. */}
      <Paper className={`${classes.wide} no-print`}>
        <div className={classes.panelTitle}>
          <span>Order Actions</span>
        </div>
        <div className={`${classes.actionRow} no-print`}>
          <Button size="small" variant="outlined" onClick={() => setActing("return")}>
            Return
          </Button>
          <Button size="small" variant="outlined" onClick={() => setActing("cancel")}>
            Cancel
          </Button>
          <Button size="small" variant="outlined" onClick={() => setActing("contact")}>
            Contact Request
          </Button>
          <Button size="small" variant="outlined" onClick={() => setActing("resend")}>
            Resend Confirmation
          </Button>
        </div>
        <div className={classes.noteHint} style={{ marginTop: 10, marginBottom: 0 }}>
          Each opens the email for review before anything is sent.
        </div>
      </Paper>

      {/* Three separate cards, as the legacy page had them: notes about the
          order, notes sent to the customer, and the email log. Lumping them
          together lost the distinction that decides who reads what. */}
      <div className={`${classes.notesGrid} print-grid`}>
        <NoteCard
          title="Order Notes"
          hint="Internal — verification and handling"
          field="notes_verification"
          value={order.notes_verification}
          orderId={order.order_id}
          onSaved={refresh}
          onEdit={() => setEditing("notes")}
        />
        <NoteCard
          title="Notes to Customer"
          hint="What the customer is told"
          field="notes_general"
          value={order.notes_general}
          orderId={order.order_id}
          onSaved={refresh}
          onEdit={() => setEditing("notes")}
        />
        <NoteCard
          title="Emails to Customer"
          hint="Written by the system"
          value={order.notes_email_log}
          readOnly
        />
      </div>

      {/* Other orders that look like the same buyer. Loaded separately: it is
          a scan across the whole order table and should not hold up the page. */}
      <Paper className={`${classes.wide} print-compact`} id="order-similar">
        <div className={classes.panelTitle}>Similar Customers</div>
        {similar === null ? (
          <div className={classes.muted} style={{ fontSize: 13 }}>Looking…</div>
        ) : !similar.length ? (
          <div className={classes.muted} style={{ fontSize: 13 }}>
            No other orders match this buyer's email, phone, name or IP.
          </div>
        ) : (
          similar.map((o) => (
            <div key={o.order_id} className={classes.similarRow}>
              <span className={classes.link} onClick={go(o.order_id)}>
                {Number(o.is_preowned) ? "P" : ""}
                {o.order_id}
              </span>{" "}
              <span className={classes.muted}>{when(o.order_time)}</span>{" "}
              {money(o.total)} · {o.status || "Pending"} · {o.title}
              {o.strength === "WEAK" ? (
                (o.match ?? []).map((m) => (
                  <span
                    key={m}
                    className={classes.badge}
                    style={{ background: MATCH_COLOR[m] || "#7f8c8d" }}
                  >
                    {m.toUpperCase()}
                  </span>
                ))
              ) : (
                <span
                  className={classes.badge}
                  style={{ background: STRENGTH_COLOR[o.strength] || "#7f8c8d" }}
                >
                  {o.strength === "STRONG" ? "SIMILAR" : o.strength}
                </span>
              )}
            </div>
          ))
        )}
      </Paper>

      <Paper className={`${classes.wide} no-print`}>
        <div className={classes.panelTitle}>Analytics</div>
        <div className={classes.pending}>
          Pending — the legacy page embedded order_analytics.php here.
        </div>
      </Paper>

      <Paper className={`${classes.wide} no-print`}>
        <div className={classes.panelTitle}>Verification (MaxMind)</div>
        <div className={classes.pending}>
          Pending — not yet decided whether TPC will use MaxMind.
        </div>
      </Paper>

      {/* One dialog drives all six panels; it is fed whichever record holds
          the fields being edited. Reloading after a save keeps the totals,
          which are recomputed from the parts, honest. */}
      {capturing && (
        <OrderCaptureDialog
          orderId={order.order_id}
          defaultAmount={grand > 0 ? grand.toFixed(2) : ""}
          onClose={() => setCapturing(false)}
          onCaptured={refresh}
        />
      )}

      {acting && (
        <OrderActionDialog
          orderId={order.order_id}
          action={acting}
          onClose={() => setActing(null)}
          onSent={refresh}
        />
      )}

      {editingItem && (
        <OrderItemDialog
          orderId={order.order_id}
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={(opts) => {
            if (!opts?.keepOpen) setEditingItem(null);
            refresh();
          }}
        />
      )}

      {editing && (
        <OrderPanelDialog
          panel={editing}
          orderId={order.order_id}
          shipRates={shipRates}
          values={{ ...order, ...(bill ?? {}), ...(ship ?? {}) }}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </Box>
  );
}

export default OrderDetailPage;
